import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AdminLogoHeader from "../../components/admin/AdminLogoHeader";
import { AuthContext } from "../../context/AuthContext";
import api from "../../services/api";
import { getRole } from "../../services/authStorage";
import { normalizeRole } from "../../utils/roles";

const initialForm = {
  first_name: "",
  middle_name: "",
  last_name: "",
  username: "",
  phone_number: "",
  date_of_birth: "",
  role: "compliance",
  admin_note: "",
  password: "",
  account_verified: true,
  kyc_approved: true,
};

const ComplianceStaffCreate = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const requesterRole = normalizeRole(user?.role || getRole());
  const roleOptions = useMemo(
    () =>
      requesterRole === "admin"
        ? [
            ["compliance", "Compliance"],
            ["accounts", "Accounts"],
            ["finance", "Finance"],
            ["admin", "Admin"],
          ]
        : [["compliance", "Compliance"]],
    [requesterRole]
  );
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setForm(initialForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess(null);
    if (!form.username.trim()) {
      setError("Email/username is required.");
      return;
    }
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError("First name and last name are required.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        ...form,
      };
      const response = await api.adminCreateStaffUser(payload);
      setSuccess(response);
      resetForm();
    } catch (err) {
      setError(err.message || "Unable to create staff user.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminLogoHeader />
      <div className="mx-auto max-w-4xl p-6 space-y-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm text-emerald-600 hover:underline"
        >
          &larr; Back to compliance
        </button>

        <section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-4">
            <h1 className="text-xl font-semibold text-slate-900">Create staff account</h1>
            <p className="text-sm text-slate-500">
              {requesterRole === "admin"
                ? "Provision an internal staff account without forcing the onboarding flow."
                : "Provision an internal compliance account without forcing the onboarding flow."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 px-6 py-6">
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            {success ? (
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <p className="font-semibold">Staff user #{success.user_id} created successfully.</p>
                {success.temporary_password ? (
                  <p className="mt-1">
                    Temporary password: <span className="font-mono font-semibold">{success.temporary_password}</span>
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">First name</label>
                <input
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Middle name</label>
                <input
                  type="text"
                  name="middle_name"
                  value={form.middle_name}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Last name</label>
                <input
                  type="text"
                  name="last_name"
                  value={form.last_name}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Email / Username</label>
                <input
                  type="email"
                  name="username"
                  value={form.username}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Phone number</label>
                <input
                  type="text"
                  name="phone_number"
                  value={form.phone_number}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Date of birth</label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={form.date_of_birth}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Role</label>
                <select
                  name="role"
                  value={form.role}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  {roleOptions.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Internal note</label>
                <textarea
                  name="admin_note"
                  value={form.admin_note}
                  onChange={onChange}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Optional note"
                />
              </div>
              <div className="md:col-span-2 grid gap-4 md:grid-cols-2">
                <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <input
                    id="staff_account_verified"
                    type="checkbox"
                    name="account_verified"
                    checked={form.account_verified}
                    readOnly
                    disabled
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <label htmlFor="staff_account_verified" className="text-sm text-slate-500">
                    Account verified
                  </label>
                </div>
                <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <input
                    id="staff_kyc_approved"
                    type="checkbox"
                    name="kyc_approved"
                    checked={form.kyc_approved}
                    readOnly
                    disabled
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <label htmlFor="staff_kyc_approved" className="text-sm text-slate-500">
                    KYC approved
                  </label>
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Temporary password (optional)</label>
              <input
                type="text"
                name="password"
                value={form.password}
                onChange={onChange}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Leave blank to auto-generate"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-emerald-600 px-6 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {submitting ? "Saving..." : "Create staff account"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ComplianceStaffCreate;
